import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
// --- 1. Import onSnapshot ---
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // --- 2. Use a ref to track the previous verification status to prevent alerts on initial load ---
  const verificationStatusRef = useRef(null);

  useEffect(() => {
    // This will be the listener's unsubscribe function
    let unsubscribeFromSnapshots = () => {};

    const unsubscribeFromAuth = onAuthStateChanged(auth, (firebaseUser) => {
      // Unsubscribe from any previous Firestore listener
      unsubscribeFromSnapshots();

      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        verificationStatusRef.current = null; // Reset on logout
        return;
      }

      try {
        const userDocRef = doc(db, 'customer', firebaseUser.uid);
        
        // --- 3. Use onSnapshot for real-time updates ---
        unsubscribeFromSnapshots = onSnapshot(userDocRef, (userDocSnap) => {
          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            const currentUserData = {
              uid: firebaseUser.uid,
              docId: firebaseUser.uid,
              id: data.id || '',
              name: data.name || firebaseUser.displayName || '',
              email: data.email || firebaseUser.email || '',
              phone: data.phone || '',
              address: data.address || '',
              location: data.location || '',
              locationId: data.locationId || '',
              // --- Make sure to include the verification status in the user object ---
              studentIdVerified: data.studentIdVerified || null,
            };

            // --- 4. LOGIC TO TRIGGER THE NOTIFICATION ---
            const previousStatus = verificationStatusRef.current;
            const newStatus = currentUserData.studentIdVerified;

            // Only show the alert if the status has *changed* from 'pending' to 'approved'
            if (previousStatus === 'pending' && newStatus === 'approved') {
              alert("Congratulations! Your Student ID has been approved.");
            }
            
            // Update the ref and the user state
            verificationStatusRef.current = newStatus;
            setUser(currentUserData);

          } else {
            // Handle case where user exists in Auth but not in Firestore
            setUser({
              uid: firebaseUser.uid,
              docId: firebaseUser.uid,
              name: firebaseUser.displayName || '',
              email: firebaseUser.email || '',
            });
          }
          setLoading(false);
        });

      } catch (error) {
        console.error('AuthContext: failed to set up user listener', error);
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeFromAuth();
      unsubscribeFromSnapshots();
    };
  }, []);

  const updateUser = (newUserData) => {
    setUser((prev) => ({ ...(prev || {}), ...(newUserData || {}) }));
  };

  const value = useMemo(() => ({ user, loading, updateUser }), [user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};